import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ReassuranceRow } from '@/components/patterns/ReassuranceRow';

/*
 * BookingSummaryCard — DESIGN_SYSTEM.md §2.4
 *
 * Sticky desktop sidebar (360px). Sections: head ("Your booking"),
 * rows (Service / Pro / When), divider, total, foot CTA, reassurance row.
 * Mobile variant lives in <StickyMobileSummary> (chrome layer, Phase 3).
 */

interface SummaryRow {
  label: ReactNode;
  /** Right-aligned value. */
  value: ReactNode;
  /** Strong emphasis on value (used for total). */
  emphasized?: boolean;
}

interface BookingSummaryCardProps {
  title?: ReactNode;
  rows: SummaryRow[];
  /** Pre-total foot rows (e.g. discounts). */
  totalLabel?: ReactNode;
  totalValue?: ReactNode;
  /** CTA button (or any node) — typically <Button block>. */
  cta?: ReactNode;
  /** Reassurance line. Defaults to Secure / Instant / SMS. Pass `null` to hide. */
  reassurance?: ReactNode | null;
  /** Make the card stick to the top on desktop. */
  sticky?: boolean;
  className?: string;
}

const DEFAULT_REASSURANCE = <ReassuranceRow />;

export function BookingSummaryCard({
  title = 'Your booking',
  rows,
  totalLabel = 'Total',
  totalValue,
  cta,
  reassurance = DEFAULT_REASSURANCE,
  sticky,
  className,
}: BookingSummaryCardProps) {
  return (
    <aside
      className={cn(
        'flex w-full flex-col rounded-3xl border border-line bg-surface shadow-card',
        sticky && 'sticky top-6',
        className,
      )}
    >
      <header className="border-b border-line px-5 py-4">
        <h3 className="text-h3 text-ink">{title}</h3>
      </header>

      <div className="flex flex-col gap-3 px-5 py-4">
        {rows.map((row, i) => (
          <div key={i} className="flex items-baseline justify-between gap-3">
            <span className="text-body-sm text-soft">{row.label}</span>
            <span
              className={cn(
                'text-body-sm text-right',
                row.emphasized ? 'font-semibold text-ink' : 'text-ink',
              )}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {totalValue && (
        <div className="flex items-baseline justify-between border-t border-line px-5 py-4">
          <span className="text-body font-semibold text-ink">{totalLabel}</span>
          <span className="text-h2 text-ink tabular-nums">{totalValue}</span>
        </div>
      )}

      {cta && <div className="border-t border-line px-5 py-4">{cta}</div>}

      {reassurance && <div className="px-5 pb-5">{reassurance}</div>}
    </aside>
  );
}
